import Delete from "@mui/icons-material/Delete";
import Restore from "@mui/icons-material/Restore";
import { IconButton, Tooltip, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { GridColDef, GridComparatorFn, GridRowId } from "@mui/x-data-grid-pro";

import CoAppDataGridDateTimeCell from "../../global/components/datagrid/CoAppDataGridDateTimeCell";
import { CoAppDataGridTextCellContainer } from "../../global/styled/global.styled";
import RuleDataGridLongTextCell from "../RuleDataGridLongTextCell";

// Calculate days until permanent deletion
export const getDifferenceInDays = (dateToCompare: Date): number => {
	const now = new Date();
	const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
	const diffInMs = Math.abs(thirtyDaysAgo.getTime() - dateToCompare.getTime());
	return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
};

const customDeletionInComparator: GridComparatorFn<number> = (v1, v2) => {
	if (v1 == null && v2 == null) return 0;
	if (v1 == null) return -1;
	if (v2 == null) return 1;
	return v1 - v2;
};

/** Row shape for trashed rules grid. */
export interface RuleTrashGridRow {
	id?: GridRowId;
	name?: string | null;
	description?: string | null;
	platform?: { name?: string } | null;
	dateTimeTrashed?: string | Date | null;
	permanentDeletionIn?: number | null;
}

export const ruleTrashColumns = ["delete", "restore", "name", "description", "permanentDeletionIn", "dateTimeTrashed", "platform"];

export const getRuleTrashColumns = (
	handleDeleteRuleToggle: (rule: RuleTrashGridRow) => void,
	handleRestoreRule: (ruleId: GridRowId) => void,
	theme: Theme
): GridColDef<RuleTrashGridRow>[] => {
	return [
		{
			field: "name",
			headerName: "Rule name",
			flex: 1,
			sortable: true,
			renderCell: (params) => (
				<RuleDataGridLongTextCell value={params.value} isTrashed={true} />
			)
		},
		{
			field: "platform",
			headerName: "Platform",
			flex: 1,
			sortable: true,
			valueGetter: (value: RuleTrashGridRow["platform"]) => value?.name,
			renderCell: (params) => (
				<RuleDataGridLongTextCell value={params.row.platform?.name || " "} />
			)
		},
		{
			field: "description",
			headerName: "Description",
			flex: 1,
			sortable: true,
			renderCell: (params) => (
				<RuleDataGridLongTextCell value={params.value} isTrashed={true} />
			)
		},
		{
			field: "permanentDeletionIn",
			headerName: "Permanent deletion in",
			flex: 1,
			sortable: true,
			sortComparator: customDeletionInComparator,
			renderCell: (params) => {
				const trashed = params.row.dateTimeTrashed;
				const diff = trashed
					? getDifferenceInDays(new Date(trashed))
					: 0;
				return (
					<CoAppDataGridTextCellContainer>
						<Typography variant="body2" color="text.disabled">{`${diff === 1 ? `${diff} day` : `${diff} days`}`}</Typography>
					</CoAppDataGridTextCellContainer>
				);
			},
			filterable: false
		},
		{
			field: "dateTimeTrashed",
			headerName: "Moved to trash",
			flex: 1,
			type: "dateTime",
			valueGetter: (value: RuleTrashGridRow["dateTimeTrashed"]) => !value ? null : new Date(value),
			sortable: true,
			renderCell: (params) => (
				<CoAppDataGridDateTimeCell value={params.value} isTrashed={true} />
			)
		},
		{
			field: "delete",
			type: "actions",
			sortable: false,
			flex: .15,
			renderCell: (params) => (
				<Tooltip title="Permanently delete" arrow placement="bottom">
					<IconButton onClick={() => handleDeleteRuleToggle(params.row)}>
						<Delete sx={{ color: theme.palette.icon.red }} />
					</IconButton>
				</Tooltip>
			)
		},
		{
			field: "restore",
			type: "actions",
			sortable: false,
			flex: .15,
			renderCell: (params) => (
				<Tooltip title="Restore" arrow placement="bottom">
					<IconButton
						onClick={() => {
							const id = params.row.id;
							if (id !== undefined && id !== null) handleRestoreRule(id);
						}}
					>
						<Restore sx={{ color: theme.palette.icon.grey }} />
					</IconButton>
				</Tooltip>
			)
		}
	];
};
